import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useStateWithCallbackInstant } from 'use-state-with-callback';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@mui/material';
import { formatDate, getFiltersFromDropdownList } from '../../lib';
import { useSelector, systemSelectors, shallowEqual } from '../../state';
import { FilterParams, SystemServiceType } from '../../types';
import {
  DataGrid,
  TextField,
  FunctionField,
  AdjustEmployeeTrackingForm,
  ListEditIcon,
  DateField,
  List,
  ExportButton,
  NotesField,
} from '..';
import { ColumnVisibilityMenu } from '../list/ColumnVisibilityMenu';
import { ActivitySectionCard } from './ActivitySectionCard';
import { useGetQueryParams, useGetUsersData } from '../../hooks';
import { format } from 'date-fns';

interface Props {
  baseUrl: string;
  serviceType: SystemServiceType;
  timezoneId?: string;
}

interface AdjustDataProps {
  trackingID: number;
  startTime: Date;
  endTime: Date;
}

export const ServiceEmployeeActivities = ({
  baseUrl,
  serviceType,
  timezoneId,
}: Props) => {
  const { classes } = useStyles();
  const { isAdmin } = useGetUsersData();
  const {
    activity,
    details,
    employee: employeeName,
    note,
    'end-time-from': endTimeFrom,
    'end-time-to': endTimeTo,
    'start-time-from': startTimeFrom,
    'start-time-to': startTimeTo,
    'user-type': userTypes,
  } = useGetQueryParams();

  const userTypesOptions = useSelector(systemSelectors.userTypes);
  const employeeActivityTypes = useSelector(
    systemSelectors.employeeActivityTypes,
  );

  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustData, setAdjustData] =
    useStateWithCallbackInstant<AdjustDataProps | null>(null, (data) =>
      setShowAdjustModal(!!data),
    );
  const [allUserTypes, setAllUserTypes] = useState({});
  const [allActivityTypes, setAllActivityTypes] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (userTypesOptions.length) {
      setAllUserTypes(getFiltersFromDropdownList(userTypesOptions));
    }
  }, [userTypesOptions]);

  useEffect(() => {
    if (employeeActivityTypes.length) {
      setAllActivityTypes(getFiltersFromDropdownList(employeeActivityTypes));
    }
  }, [employeeActivityTypes]);

  const handleEditClick = useCallback(
    (record) => {
      const { trackingID, startTime, endTime } = record;

      setAdjustData({
        startTime,
        endTime,
        trackingID,
      });
    },
    [setAdjustData],
  );

  const apiFilters = useMemo(
    () => ({
      activityTypes: activity,
      details,
      employeeName,
      endTimeFrom,
      endTimeTo,
      note,
      startTimeFrom,
      startTimeTo,
      userTypes,
    }),
    [
      activity,
      details,
      employeeName,
      endTimeFrom,
      endTimeTo,
      note,
      startTimeFrom,
      startTimeTo,
      userTypes,
    ],
  );

  const uiFilters: FilterParams[] = useMemo(() => {
    const rv: FilterParams[] = [
      {
        queryParam: 'employee',
        label: 'Employee',
        filterType: 'search',
      },
      {
        filter: allUserTypes,
        queryParam: 'user-type',
        label: 'User type',
      },
      {
        filter: allActivityTypes,
        queryParam: 'activity',
        label: 'Activity',
      },
      {
        queryParams: { fromParam: 'start-time-from', toParam: 'start-time-to' },
        label: 'Start time',
        filterType: 'dateTimeRange',
      },
      {
        queryParams: { fromParam: 'end-time-from', toParam: 'end-time-to' },
        label: 'End time',
        filterType: 'dateTimeRange',
      },
      {
        queryParam: 'note',
        label: 'Note',
        filterType: 'search',
      },
      {
        queryParam: 'details',
        label: 'Details',
        filterType: 'search',
      },
    ];
    return rv;
  }, [allActivityTypes, allUserTypes]);

  const listResource = `${serviceType.toLowerCase()}-employee-activities`;
  const columnVisibility = useSelector(
    (state: any) =>
      state.lists?.resources?.[listResource]?.params?.columnVisibility ?? {},
    shallowEqual,
  );

  const exportParams = useMemo(
    () => generateExportParams({ baseUrl, serviceType }),
    [baseUrl, serviceType],
  );

  if (!isAdmin) return null;

  return (
    <ActivitySectionCard
      title='Employee activity log'
      actions={
        <>
          <ColumnVisibilityMenu
            resource={listResource}
            fields={columnFieldsDef}
            columnVisibility={columnVisibility}
          />
          <ExportButton
            headersArray={exportParams.exportFields}
            path={exportParams.exportURLPath}
            queryParams={{
              ...(exportParams.exportURLQuery || {}),
              ...apiFilters,
            }}
            fileName={exportParams.exportFileName}
          />
        </>
      }
    >
      <List
        actions={null}
        baseUrl={`${baseUrl}/employee-activities`}
        resource={`${serviceType.toLowerCase()}-employee-activities`}
        filter={apiFilters}
        bulkActionButtons={false}
        orderBy='id'
        order='asc'
        clearStateOnExit
        filtersProps={{
          hasMultipleFilters: true,
          filters: uiFilters,
          hideFilterToggle: true,
          hideColumnVisibility: true,
        }}
        paperClassName={classes.paper}
        rootClassName={classes.list}
        hasDefaultSearchField={false}
        defaultState={
          <Typography style={{ padding: 24 }}>No results found</Typography>
        }
      >
        <DataGrid>
          <TextField
            source='actionDoneByName'
            label='Employee'
            sortable={false}
          />
          <TextField
            source='userTypeDisplay'
            label='User type'
            sortable={false}
            headerClassName={classes.noWrap}
          />
          <TextField
            source='activityTypeDisplay'
            label='Activity'
            sortable={false}
          />
          <DateField
            source='startTime'
            label='Start'
            sortable={false}
            format='Pp zzz'
            timeZone={timezoneId}
            useTime
            headerClassName={classes.noWrap}
          />
          <DateField
            source='endTime'
            label='End'
            sortable={false}
            format='Pp zzz'
            timeZone={timezoneId}
            useTime
            headerClassName={classes.noWrap}
          />
          <TextField
            source='durationDisplay'
            label='Duration'
            sortable={false}
            className={classes.duration}
          />
          <NotesField
            source='note'
            label='Note'
            sortable={false}
            maxCharacterLength={30}
          />
          <TextField source='details' label='Details' sortable={false} />
          <FunctionField
            source=''
            label=''
            sortable={false}
            render={(record) => {
              const { trackingID } = record;
              return trackingID ? (
                <ListEditIcon
                  wrapWithButton
                  iconButtonProps={{
                    'aria-label': 'Adjust employee activity time',
                    title: 'Adjust employee activity time',
                    onClick: () => handleEditClick(record),
                  }}
                />
              ) : null;
            }}
          />
        </DataGrid>
      </List>
      {showAdjustModal && !!adjustData?.trackingID && (
        <AdjustEmployeeTrackingForm
          adjustActivityFormType='Employee'
          open={showAdjustModal}
          handleClose={() => setAdjustData(null)}
          startTime={adjustData?.startTime}
          endTime={adjustData?.endTime}
          trackingId={adjustData.trackingID}
        />
      )}
    </ActivitySectionCard>
  );
};

const columnFieldsDef = [
  <TextField key='e' source='actionDoneByName' label='Employee' />,
  <TextField key='u' source='userTypeDisplay' label='User type' />,
  <TextField key='a' source='activityTypeDisplay' label='Activity' />,
  <DateField key='s' source='startTime' label='Start' />,
  <DateField key='en' source='endTime' label='End' />,
  <TextField key='d' source='durationDisplay' label='Duration' />,
  <NotesField key='n' source='note' label='Note' maxCharacterLength={30} />,
  <TextField key='de' source='details' label='Details' />,
];

const exportFields = [
  { key: 'actionDoneByName', label: 'Employee' },
  { key: 'activityTypeDisplay', label: 'Activity' },
  {
    key: 'startTime',
    label: 'Start',
    formatFunc: (row) =>
      formatDate(row.startTime, {
        formatStr: 'Pp zzz',
        useTime: true,
        timeZone: row.warehouseTimezoneID,
      }),
  },
  {
    key: 'endTime',
    label: 'End',
    formatFunc: (row) =>
      formatDate(row.endTime, {
        formatStr: 'Pp zzz',
        useTime: true,
        timeZone: row.warehouseTimezoneID,
      }),
  },
  { key: 'durationDisplay', label: 'Duration' },
  { key: 'note', label: 'Note' },
  { key: 'details', label: 'Details' },
];

const generateExportParams = ({ baseUrl, serviceType }) => {
  return {
    exportURLPath: `${baseUrl}/employee-activities`,
    exportURLQuery: { IsExport: true },
    exportFields: exportFields,
    exportFileName: `${serviceType}_employee_tracking_${format(
      new Date(),
      'Pp',
    )}`,
  };
};

const useStyles = makeStyles({ name: { ServiceEmployeeActivities } })(
  (theme) => ({
    duration: {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
    noWrap: {
      whiteSpace: 'nowrap',
    },
    list: {
      padding: 0,
    },
    paper: {
      boxShadow: 'none',
      marginBottom: 0,
    },
  }),
);
